import { lazy, Suspense, type ReactNode } from 'react';
import type { Api } from './api';
import { AuthProvider } from './auth/AuthProvider';
import { HostBranding } from './branding/Branding';
import { ThemeProvider } from './theme/ThemeProvider';
import type { Theme } from './theme/theme';

// The installed-app layer (service worker, update and install prompts, src/pwa): its own
// chunk, fetched after the load event so it never competes with the first load
const PwaLayer = lazy(() =>
  new Promise((done) => (document.readyState === 'complete' ? done(0) : addEventListener('load', done, { once: true })))
    .then(() => import('./pwa/PwaLayer'))
    .then((m) => ({ default: m.PwaLayer }))
);

/** `pwa`: mount the installed-app layer (off in unit tests unless asked for) */
export function Providers({ api, theme, pwa = import.meta.env.MODE !== 'test', children }: { api: Api; theme?: Theme; pwa?: boolean; children: ReactNode }) {
  return (
    <ThemeProvider {...(theme ? { initialTheme: theme } : {})}>
      <HostBranding api={api} />
      <AuthProvider api={api}>
        {children}
        {pwa ? (
          <Suspense fallback={null}>
            <PwaLayer api={api} />
          </Suspense>
        ) : null}
      </AuthProvider>
    </ThemeProvider>
  );
}
